/**
 * FollowUpTab.jsx — Open House Planner follow-up queue (/openhouse?tab=followup)
 *
 * Turns field visits into CRM motion. Every stop marked done in Run Mode
 * lands here, grouped by plan (newest first). Per stop: link or create the
 * CRM contact, mint a tracked short link for the delivered video (with live
 * click count), compose a follow-up text from templates, copy it, and log it
 * as an outreach action — which feeds the existing Outreach XP/cadence
 * engine. 'Log as sent' sets outcome.followUpSent on the stop.
 */

import { useState, useMemo, useEffect } from 'react';
import {
  Box, Stack, Typography, Paper, Chip, Button, IconButton, TextField,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Switch,
  Dialog, DialogTitle, DialogContent, DialogActions, List, ListItemButton,
  ListItemText, InputAdornment, CircularProgress, Divider, Tooltip,
  Link as MuiLink,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  PersonAdd as PersonAddIcon,
  Link as LinkIcon,
  Search as SearchIcon,
  Send as SendIcon,
  CheckCircle as CheckCircleIcon,
  Instagram as InstagramIcon,
  Phone as PhoneIcon,
  HomeWork as HouseIcon,
} from '@mui/icons-material';
import {
  useOpenHouses, useUpdateOpenHouse, useOhPlans, useAllOhPlanStops,
  useUpdateOhPlanStop,
} from '../../hooks/useOpenHouses';
import { useContacts, useCreateContact } from '../../hooks/useContacts';
import { useLinks, useCreateLink } from '../../hooks/useLinks';
import { useLogOutreachAction } from '../../hooks/useOutreach';
import { useAuth } from '../../hooks/useAuth';
import { useMobile } from '../../hooks/useMobile';
import { useToast } from '../layout/Toast';
import { logAction } from '../../lib/actionLogger';
import { fmtD } from '../../lib/utils';
import { FOLLOWUP_TEMPLATES, fillOhTemplate } from '../../lib/openHouseTemplates';

const VILLAGE_COLOR = '#E879F9';
const SHORT_BASE = 'https://portal.rotogears.com/r/';

function isVillage(brokerage) {
  return (brokerage || '').toLowerCase().includes('village');
}

/** '123 Main St, Santa Barbara, CA' → '123 Main St' */
function shortAddress(address) {
  return String(address || '').split(',')[0].trim();
}

// ── Link-contact dialog ─────────────────────────────────────────────────

function LinkContactDialog({ open, onClose, house, agentCell }) {
  const { toast } = useToast();
  const { isMobile } = useMobile();
  const { profile } = useAuth();
  const { data: contacts = [] } = useContacts();
  const updateOh = useUpdateOpenHouse();
  const createContact = useCreateContact();

  const [query, setQuery] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setQuery(house?.agentName || ''); setSaving(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return contacts
      .filter((c) => (c.name || '').toLowerCase().includes(q))
      .slice(0, 20);
  }, [contacts, query]);

  const linkTo = async (contactId, label) => {
    setSaving(true);
    try {
      await updateOh.mutateAsync({ id: house.id, contactId });
      logAction({
        action: 'Link OH Contact',
        user: profile?.full_name || profile?.email,
        description: `Linked ${label} to open house at ${shortAddress(house.address)}`,
        details: { openHouseId: house.id, contactId },
      });
      toast(`Linked to ${label}`, 'success');
      onClose();
    } catch (e) {
      toast(`Link failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    const name = (house?.agentName || query).trim();
    if (!name) { toast('Need an agent name to create a prospect', 'error'); return; }
    setSaving(true);
    try {
      const created = await createContact.mutateAsync({
        name,
        company: house?.brokerage || null,
        phone: agentCell || house?.agentPhone || null,
        type: 'prospect',
        data: { tags: ['open-house'], outreach_status: 'contacted', source: 'open_house' },
      });
      await updateOh.mutateAsync({ id: house.id, contactId: created.id });
      logAction({
        action: 'Create OH Prospect',
        user: profile?.full_name || profile?.email,
        description: `Created prospect ${name} from open house at ${shortAddress(house.address)}`,
        details: { openHouseId: house.id, contactId: created.id, brokerage: house?.brokerage },
      });
      toast(`Prospect created: ${name}`, 'success');
      onClose();
    } catch (e) {
      toast(`Create failed: ${e.message}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs" fullScreen={isMobile}>
      <DialogTitle>Link contact — {shortAddress(house?.address)}</DialogTitle>
      <DialogContent>
        <TextField
          size="small"
          fullWidth
          autoFocus
          placeholder="Search contacts by name"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
          }}
          sx={{ mt: 0.5 }}
        />
        <List dense sx={{ maxHeight: 280, overflowY: 'auto' }}>
          {matches.map((c) => (
            <ListItemButton key={c.id} data-track="openhouse.linkContactPick" disabled={saving} onClick={() => linkTo(c.id, c.name)}>
              <ListItemText primary={c.name} secondary={c.company || c.type} />
            </ListItemButton>
          ))}
          {query.trim() && matches.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
              No matches — create a prospect instead.
            </Typography>
          )}
        </List>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleCreate}
          disabled={saving || !(house?.agentName || query.trim())}
          startIcon={saving ? <CircularProgress size={14} /> : <PersonAddIcon />}
        >
          Create prospect
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Stop card ───────────────────────────────────────────────────────────

function StopCard({ stop, house, links }) {
  const { toast } = useToast();
  const { profile } = useAuth();
  const { isMobile } = useMobile();
  const updateStop = useUpdateOhPlanStop();
  const createLink = useCreateLink();
  const logOutreach = useLogOutreachAction();

  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [message, setMessage] = useState('');
  const [creatingLink, setCreatingLink] = useState(false);
  const [logging, setLogging] = useState(false);

  const outcome = stop.outcome || {};
  const sent = !!outcome.followUpSent;
  const addr = shortAddress(house?.address) || 'Unknown address';
  const agentCell = outcome.agentCell || null;
  const trackedLink = outcome.linkId ? links.find((l) => l.id === outcome.linkId) : null;
  const shortUrl = outcome.shortCode ? `${SHORT_BASE}${outcome.shortCode}` : null;

  const templateCtx = {
    firstName: (house?.agentName || '').trim().split(/\s+/)[0] || undefined,
    address: addr || undefined,
    videoLink: shortUrl || undefined,
    senderName: profile?.full_name?.split(' ')[0] || 'Cyprian',
  };

  const handleTemplate = (id) => {
    setTemplateId(id);
    const tpl = FOLLOWUP_TEMPLATES.find((t) => t.id === id);
    if (tpl) setMessage(fillOhTemplate(tpl.body, templateCtx));
  };

  const handleCreateTrackedLink = async () => {
    let dest = videoUrl.trim();
    if (!dest) return;
    if (!/^https?:\/\//i.test(dest)) dest = `https://${dest}`;
    try { new URL(dest); } catch { toast('That does not look like a URL', 'error'); return; }
    setCreatingLink(true);
    try {
      const data = await createLink.mutateAsync({
        destination_url: dest,
        title: `OH reel — ${addr}`,
        tags: ['open-house'],
      });
      await updateStop.mutateAsync({
        id: stop.id,
        outcome: { ...outcome, shortCode: data.short_code, linkId: data.id },
      });
      setVideoUrl('');
      toast(`Tracked link created — /r/${data.short_code}`, 'success');
    } catch (e) {
      toast(`Link failed: ${e.message}`, 'error');
    } finally {
      setCreatingLink(false);
    }
  };

  const handleCopy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast(`${label} copied`, 'success');
    } catch {
      toast('Copy failed — select and copy manually', 'error');
    }
  };

  const handleLogSent = async () => {
    if (!house?.contactId || !message.trim()) return;
    setLogging(true);
    try {
      await logOutreach.mutateAsync({
        contactId: house.contactId,
        userId: profile?.id,
        actionType: 'follow_up',
        channel: 'text',
        nature: 'business',
        notes: `Open house follow-up — ${addr}`,
        messageBody: message.trim(),
      });
      await updateStop.mutateAsync({ id: stop.id, outcome: { ...outcome, followUpSent: true } });
      logAction({
        action: 'OH Follow-Up Sent',
        user: profile?.full_name || profile?.email,
        description: `Logged open house follow-up text for ${addr}`,
        details: { stopId: stop.id, openHouseId: house.id, contactId: house.contactId, templateId },
      });
      toast('Follow-up logged to Outreach', 'success');
    } catch (e) {
      toast(`Log failed: ${e.message}`, 'error');
    } finally {
      setLogging(false);
    }
  };

  // Outcome chips
  const chips = [];
  if (outcome.shootType === 'teaser') chips.push(['Teaser shot', 'info']);
  if (outcome.shootType === 'agent_on_camera') chips.push(['Agent on camera', 'secondary']);
  if (outcome.gotCell || outcome.agentCell) chips.push(['Got cell', 'success']);
  if (!outcome.shootType || outcome.shootType === 'none') chips.push(['No shoot', 'default']);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, opacity: sent ? 0.85 : 1 }}>
      {/* Header: address + chips */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={600} noWrap>{addr}</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.25 }}>
            {house?.agentName && (
              <Typography variant="caption" fontWeight={500}>{house.agentName}</Typography>
            )}
            {agentCell && (
              <MuiLink href={`tel:${agentCell}`} variant="caption" underline="hover" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
                <PhoneIcon sx={{ fontSize: 12 }} /> {agentCell}
              </MuiLink>
            )}
            {outcome.agentIg && (
              <MuiLink
                href={`https://instagram.com/${String(outcome.agentIg).replace(/^@/, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                variant="caption"
                underline="hover"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
              >
                <InstagramIcon sx={{ fontSize: 12 }} /> @{String(outcome.agentIg).replace(/^@/, '')}
              </MuiLink>
            )}
          </Stack>
        </Box>
        {sent && <Chip size="small" icon={<CheckCircleIcon />} color="success" label="Followed up" sx={{ height: 22, flexShrink: 0 }} />}
      </Stack>

      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
        {house?.brokerage && (
          <Chip
            size="small"
            label={house.brokerage}
            sx={{
              height: 18, fontSize: 10, maxWidth: 160,
              ...(isVillage(house.brokerage) ? { bgcolor: VILLAGE_COLOR, color: '#fff', fontWeight: 600 } : {}),
            }}
          />
        )}
        {chips.map(([label, color]) => (
          <Chip key={label} size="small" color={color === 'default' ? undefined : color} variant="outlined" label={label} sx={{ height: 18, fontSize: 10 }} />
        ))}
        <Box sx={{ flex: 1 }} />
        {house?.contactId ? (
          <Chip size="small" color="success" variant="outlined" label="In CRM" sx={{ height: 18, fontSize: 10 }} />
        ) : (
          <Button size="small" startIcon={<PersonAddIcon sx={{ fontSize: 14 }} />} onClick={() => setLinkDialogOpen(true)} sx={{ py: 0, minHeight: 24 }}>
            Link contact
          </Button>
        )}
      </Stack>

      {/* Tracked video link */}
      <Box sx={{ mt: 1 }}>
        {shortUrl ? (
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
            <LinkIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>{shortUrl}</Typography>
            <Tooltip title="Copy link">
              <IconButton size="small" onClick={() => handleCopy(shortUrl, 'Link')}>
                <CopyIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
            <Chip
              size="small"
              variant="outlined"
              color={(trackedLink?.click_count || 0) > 0 ? 'success' : 'default'}
              label={`clicked ${trackedLink?.click_count ?? 0}×`}
              sx={{ height: 18, fontSize: 10 }}
            />
          </Stack>
        ) : (
          <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems={isMobile ? 'stretch' : 'center'}>
            <TextField
              size="small"
              fullWidth
              placeholder="Paste video URL (Frame.io/Vimeo/Drive)"
              value={videoUrl}
              onChange={(e) => setVideoUrl(e.target.value)}
              sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={handleCreateTrackedLink}
              disabled={creatingLink || !videoUrl.trim()}
              startIcon={creatingLink ? <CircularProgress size={12} /> : <LinkIcon sx={{ fontSize: 14 }} />}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              Create tracked link
            </Button>
          </Stack>
        )}
      </Box>

      {/* Composer */}
      {!sent && (
        <>
          <Divider sx={{ my: 1.25 }} />
          <Stack spacing={1}>
            <FormControl size="small" fullWidth>
              <InputLabel>Follow-up template</InputLabel>
              <Select label="Follow-up template" value={templateId} onChange={(e) => handleTemplate(e.target.value)}>
                {FOLLOWUP_TEMPLATES.map((t) => (
                  <MenuItem key={t.id} value={t.id}>{t.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {templateId && (
              <>
                <TextField
                  size="small"
                  multiline
                  minRows={3}
                  fullWidth
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  sx={{ '& .MuiInputBase-input': { fontSize: 13 } }}
                />
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<CopyIcon sx={{ fontSize: 14 }} />}
                    onClick={() => handleCopy(message, 'Text')}
                    disabled={!message.trim()}
                  >
                    Copy text
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    startIcon={logging ? <CircularProgress size={12} color="inherit" /> : <SendIcon sx={{ fontSize: 14 }} />}
                    onClick={handleLogSent}
                    disabled={logging || !message.trim() || !house?.contactId}
                  >
                    Log as sent
                  </Button>
                  {!house?.contactId && (
                    <Typography variant="caption" color="text.secondary">
                      Link a contact first so it lands in Outreach history
                    </Typography>
                  )}
                </Stack>
              </>
            )}
          </Stack>
        </>
      )}

      {house && (
        <LinkContactDialog
          open={linkDialogOpen}
          onClose={() => setLinkDialogOpen(false)}
          house={house}
          agentCell={agentCell}
        />
      )}
    </Paper>
  );
}

// ── Main tab ────────────────────────────────────────────────────────────

export default function FollowUpTab() {
  const { data: stops = [], isLoading: stopsLoading } = useAllOhPlanStops();
  const { data: plans = [], isLoading: plansLoading } = useOhPlans();
  const { data: openHouses = [] } = useOpenHouses();
  const { data: links = [] } = useLinks();

  const [showCompleted, setShowCompleted] = useState(false);

  const houseById = useMemo(() => {
    const m = new Map();
    for (const oh of openHouses) m.set(oh.id, oh);
    return m;
  }, [openHouses]);

  // Done stops where a conversation happened (talked !== false), grouped by plan.
  const { groups, doneCount, completedCount } = useMemo(() => {
    const done = stops.filter((s) => s.status === 'done' && s.outcome?.talked !== false);
    const completed = done.filter((s) => s.outcome?.followUpSent);
    const visible = showCompleted ? done : done.filter((s) => !s.outcome?.followUpSent);

    const byPlan = new Map();
    for (const s of visible) {
      if (!byPlan.has(s.planId)) byPlan.set(s.planId, []);
      byPlan.get(s.planId).push(s);
    }
    const planById = new Map(plans.map((p) => [p.id, p]));
    const result = [...byPlan.entries()]
      .map(([planId, planStops]) => ({
        plan: planById.get(planId) || { id: planId, title: 'Untitled plan', planDate: null },
        stops: planStops.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)),
      }))
      .sort((a, b) =>
        String(b.plan.planDate || '').localeCompare(String(a.plan.planDate || ''))
        || String(b.plan.createdAt || '').localeCompare(String(a.plan.createdAt || '')));
    return { groups: result, doneCount: done.length, completedCount: completed.length };
  }, [stops, plans, showCompleted]);

  if (stopsLoading || plansLoading) {
    return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>;
  }

  if (doneCount === 0) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <HouseIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
        <Typography variant="subtitle1" fontWeight={600}>No visits logged yet — run a plan first.</Typography>
        <Typography variant="body2" color="text.secondary">
          Stops you mark done in Run Mode land here for follow-up.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ maxWidth: 760 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {doneCount - completedCount} pending · {completedCount} sent
        </Typography>
        <FormControlLabel
          control={<Switch size="small" checked={showCompleted} onChange={(e) => setShowCompleted(e.target.checked)} />}
          label={<Typography variant="body2">Show completed</Typography>}
        />
      </Stack>

      {groups.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
          <CheckCircleIcon sx={{ fontSize: 32, color: 'success.main', mb: 0.5 }} />
          <Typography variant="subtitle2" fontWeight={600}>
            All caught up — {completedCount} follow-up{completedCount === 1 ? '' : 's'} sent
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {groups.map(({ plan, stops: planStops }) => (
            <Box key={plan.id}>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mb: 0.5, lineHeight: 1.5 }}>
                {plan.title || 'Untitled plan'} — {fmtD(plan.planDate)}
              </Typography>
              <Stack spacing={1}>
                {planStops.map((stop) => (
                  <StopCard
                    key={stop.id}
                    stop={stop}
                    house={houseById.get(stop.openHouseId)}
                    links={links}
                  />
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
